import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, JoyKitThemeProvider, Box, ButtonV2 } from '@withjoy/joykit';
import { FiltersPanel } from './components/FiltersPanel/FiltersPanel';
import { ListingsPanel } from './components/ListingsPanel/ListingsPanel';
import { Map } from './components/Map/Map';
import { ListButton } from './components/Map/components/ListButton';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useListingFilters } from './hooks/useListingFilters';
import { useListingSort } from './hooks/useListingSort';
import { useListingSortingByViewport } from './hooks/useListingSortingByViewport';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { AccommodationsConsoleProps, AccommodationsConsoleState, AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { useAccommodationsConsoleListings } from './hooks/useAccommodationsConsoleListings';
import { AccommodationsConsoleHeader } from './components/Header';
import { EmptyState } from './components/EmptyState';
import { ErrorState } from './components/ErrorState';
import { AccommodationsConsoleErrorBoundary } from './components/AccommodationsConsoleErrorBoundary';
import { differenceInCalendarDays } from 'date-fns';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { usePagination } from './hooks/usePagination';
import { ShowMoreButton } from './components/ListingsPanel';
import { MobileSearchComponent } from './components/MobileSearchBar';
import { SingleHotelView } from './components/SingleHotelView/SingleHotelView';
import { BottomSheet } from '@apps/stays/components/BottomSheet';
import { useAccommodationsConsoleHealth, wrapWithAccommodationsConsoleProvider } from './AccommodationsConsole.health';
import type { UseAccommodationsConsoleSearchParamsStateReturn } from './hooks/useAccommodationsConsoleSearchParamsState';
import { useAccommodationsConsoleSearchParamsState } from './hooks/useAccommodationsConsoleSearchParamsState';
import { useMobileSheetDisclosures } from './hooks/useMobileSheetDisclosures';
import { isValidCoordinates } from '@shared/core/places';
import { useMapTelemetry } from './components/Map/Map.telemetry';
import { Map as MapIcon } from '@withjoy/joykit/icons';

import { useMapControls } from './hooks/useMapControls';
import { MapLoader } from './components/Map/MapLoader';
import { useAccommodationRedirectUrl, createUtmParametersForAccommodationsConsolePage } from './hooks/useAccommodationRedirectUrl';
import { MobileTransparentDrawer } from '@shared/components/MobileTransparentDrawer';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { useTranslation } from '@shared/core';
import { useListingsPanelTelemetry } from './components/ListingsPanel/ListingsPanel.telemetry';
import { AccommodationsConsoleLoader } from './components/AccommodationsConsoleLoader/AccommodationsConsoleLoader';
import { ProgressiveLoader } from '@apps/stays/components/ProgressiveLoader';
import { withWindow } from '@shared/utils/withWindow';
import { useAccommodationsConsoleTelemetry } from './AccommodationsConsole.telemetry';
import type { LoadStatus } from '@apps/stays/types';
import { useMount } from '@shared/utils/hooks/useMount';
import { AccommodationMapListSync } from './components/AccommodationMapListSync/AccommodationMapListSync';
import { resolveScenarioFlags } from './scenarios/scenarioFlags';
import type { ScenarioFlags } from './scenarios/scenarios.types';
import type { LatLngLiteral } from '@shared/core/google-maps/types';
import { HotelSearchMapLayout, SortOption } from '@shared/constants/accommodations';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import { getSelectedRedirectTelemetryName } from '@shared/utils/extractHotelRedirects';
import { ScrollTopContainer } from '@shared/components/MobileTransparentDrawer/MobileTransparentDrawer.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { readNavigatorLanguage } from '@shared/utils/getNavigatorLanguage';
import { translatedPrice } from '@shared/utils/accommodationPrice';
import { HotelOfferDiagnosticsProvider } from '@apps/stays/EventAccommodations/routes/shared/hotelOfferDiagnostics/HotelOfferDiagnosticsContext';

// Owns mobile drawer open/close state. Map content (children) and listings panel
// are passed from the parent and won't re-render when the drawer toggles.
type MobileMapDrawerLayoutProps = Readonly<{
  children: React.ReactNode;
  headerHeight: number;
  listingsPanelContent: React.ReactNode;
  /** Optional drawer sheet background (cash-fund cream); defaults to the drawer's white. */
  drawerBackgroundColor?: string;
}>;

const MobileMapDrawerLayout = React.memo(function MobileMapDrawerLayout({ children, headerHeight, listingsPanelContent, drawerBackgroundColor }: MobileMapDrawerLayoutProps) {
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(true);
  const { tq } = useTranslation('accommodationsConsole');

  return (
    <>
      <Content>
        {!isMobileDrawerOpen && (
          <>
            {children}
            <ScrollTopContainer>
              <ButtonV2
                size="md"
                paddingY={4}
                shape="rounded"
                intent="neutral"
                minWidth={pxToRem(164)}
                typographyVariant="button2"
                onClick={() => setIsMobileDrawerOpen(true)}
              >
                {tq`View List`}
              </ButtonV2>
            </ScrollTopContainer>
          </>
        )}
      </Content>
      {isMobileDrawerOpen ? (
        <MobileTransparentDrawer
          scrollTopCta={{ icon: MapIcon, text: tq`Map`, closeOnClick: true }}
          isOpenByDefault={true}
          onOpenChange={setIsMobileDrawerOpen}
          closedHeightPx={0}
          animationType="spring"
          openPositionVh={0}
          openPositionOffsetPx={headerHeight}
          backgroundColor={drawerBackgroundColor}
          showScrollCtaByDefault={true}
        >
          {listingsPanelContent}
        </MobileTransparentDrawer>
      ) : null}
    </>
  );
});

type RootProps = Readonly<{
  state: AccommodationsConsoleState;
  onChangeLocation: UseAccommodationsConsoleSearchParamsStateReturn['handleOnChangeLocation'];
  onHeaderSearchSubmit: UseAccommodationsConsoleSearchParamsStateReturn['handleHeaderSearchFormSubmit'];
  selectedHotelId?: string | null;
  isInitialLoad: boolean;
  isShowingAllCouplesPicks: boolean;
  scenarioFlags: ScenarioFlags;
}> &
  Pick<
    AccommodationsConsoleProps,
    | 'eventHandle'
    | 'eventName'
    | 'onClose'
    | 'scenarioConfig'
    | 'renderListingEntry'
    | 'renderListingsHeader'
    | 'renderFiltersHeader'
    | 'onMarkerClick'
    | 'onListingsSettled'
    | 'markerClickFrom'
  >;

const Root = ({
  state,
  eventHandle,
  eventName,
  onClose,
  onChangeLocation,
  onHeaderSearchSubmit,
  selectedHotelId,
  isInitialLoad,
  isShowingAllCouplesPicks,
  renderListingEntry,
  renderListingsHeader,
  renderFiltersHeader,
  onMarkerClick,
  onListingsSettled,
  scenarioConfig,
  scenarioFlags,
  markerClickFrom,
}: RootProps) => {
  const { topBar, mapLayoutMode } = scenarioConfig;
  const startTimeRef = useRef(Date.now());
  const hasFiredResultsLoadedRef = useRef(false);
  // Tracks whether a listing fetch has actually started — prevents premature firing
  // when isCombinedLoading is false because the query hasn't been dispatched yet
  const hasFetchStartedRef = useRef(false);
  const {
    sourceName,
    skipCouplePicks,
    includeCouplesPicks,
    hideEventNameAndNote,
    showAvailableOnlyTooltip,
    showJoyTravelBenefitsFilter,
    defaultSortOption,
    shouldFilterOutNonJoyRoomBlocks,
    shouldShowVenueMapPin,
    showFundSavingsSort,
  } = scenarioFlags;

  const telemetry = useMapTelemetry();
  const { isFilterPanelOpen, onFilterPanelOpen, onFilterPanelClose, isSearchOpen, onSearchOpen, onSearchClose } = useMobileSheetDisclosures();

  const isMobileOrTablet = useIsMobileOrTablet();
  const { onLoadComplete, onLoadFailure } = useAccommodationsConsoleHealth();
  const [hidePanel, togglePanel] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<AccommodationListingDTO | null>(null);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [headerSize, headerRef] = useSizeObserver();
  const { mapViewPort, setMapViewPort, reCenterMap, recenterMapViaLocation, setReCenterMap, recenterMapViaMarkers } = useMapControls();

  const isMapOnlyMode = mapLayoutMode === HotelSearchMapLayout.MAP_ONLY;
  const isCompactMapMode = !isMobileOrTablet && mapLayoutMode === HotelSearchMapLayout.COMPACT;
  const shouldShowFullMap = isMapExpanded || !isCompactMapMode;

  // If skipCouplePicks is true or we're in compact mode (where map is hidden initially),
  // disable couple picks mode to ensure consistent behavior
  const effectiveIsShowingAllCouplesPicks = skipCouplePicks || isCompactMapMode ? false : isShowingAllCouplesPicks;

  // Stabilize callbacks to prevent unnecessary re-renders in child hooks
  const handleLoadError = useEventCallback((error: unknown) => {
    onLoadFailure(error, { state, filters });
  });

  const handleLoadSuccess = useEventCallback(() => {
    onLoadComplete();
  });

  const {
    cachedListings,
    listings,
    hasAccommodationPlacesByCouple,
    isFetchingListings,
    isFetchingListingPrices,
    pricingError,
    isCombinedLoading,
    isUsingBackgroundListings,
    hasApiError,
  } = useAccommodationsConsoleListings({
    state,
    variant: effectiveIsShowingAllCouplesPicks ? 'couple' : 'regional',
    skipCouplePicks: skipCouplePicks,
    shouldFilterOutNonJoyRoomBlocks,
    eventHandle,
    onError: handleLoadError,
    onSuccess: handleLoadSuccess,
    isInitialLoad,
  });

  const {
    filters,
    resetFilters,
    updateFilters,
    facilityCounts,
    starRatingCounts,
    guestRatingCounts,
    propertyCategoryCounts,
    brandCounts,
    propertyNameSearch,
    filteredProperties,
    supportedFacilities,
    supportedBrands,
    setPropertyNameSearch,
    dynamicPriceMax,
    listingPrices,
  } = useListingFilters(listings, isFetchingListingPrices, defaultSortOption);

  // Notify parent when listings settle (including empty results)
  useEffect(() => {
    if (isCombinedLoading || !onListingsSettled) return;
    const venue = state.circularRegionSearch;
    onListingsSettled(listings, venue ? { latitude: venue.latitude, longitude: venue.longitude } : null);
  }, [isCombinedLoading, listings, state.circularRegionSearch, onListingsSettled]);

  const { onAccommodationsResultsLoaded } = useAccommodationsConsoleTelemetry();
  const stableOnAccommodationsResultsLoaded = useEventCallback(onAccommodationsResultsLoaded);

  // Track when a fetch actually starts so we don't fire before queries are dispatched
  useEffect(() => {
    if (isFetchingListings || isCombinedLoading) {
      hasFetchStartedRef.current = true;
    }
  }, [isFetchingListings, isCombinedLoading]);

  // Fire once when listings settle — measures user-perceived wait time from component mount
  useEffect(() => {
    if (hasFiredResultsLoadedRef.current || isCombinedLoading || !hasFetchStartedRef.current) return;

    let load_status: LoadStatus;
    // Check data presence first — cached listings may be visible even if the fresh fetch errored
    if (listings.length > 0) {
      load_status = 'success';
    } else if (hasApiError) {
      load_status = 'error';
    } else {
      load_status = 'empty';
    }

    stableOnAccommodationsResultsLoaded({
      accommodations_results_loaded_ms: Math.round(Date.now() - startTimeRef.current),
      load_status,
    });
    hasFiredResultsLoadedRef.current = true;
  }, [isCombinedLoading, isFetchingListings, hasApiError, listings.length, stableOnAccommodationsResultsLoaded]);

  const sortListingsInViewport = useListingSortingByViewport();

  // Cash-fund "Save more, travel further" toggle: keep only stays the fund fully covers
  // (out-of-pocket $0, i.e. stayTotal ≤ balance). No-op for other variants (balance undefined).
  const fundCoveredProperties = useMemo(() => {
    const balance = scenarioConfig.markerOutOfPocketBalance;
    if (!filters.cashFundFullyCoveredOnly || balance == null) return filteredProperties;
    return filteredProperties.filter(listing => {
      const stayTotal = listing.pricingInfo?.values?.totalRate;
      return stayTotal != null && stayTotal <= balance;
    });
  }, [filteredProperties, filters.cashFundFullyCoveredOnly, scenarioConfig.markerOutOfPocketBalance]);

  // Filter hotels as per map visible port
  const listingsInViewport = useMemo(() => {
    if (shouldShowFullMap && !isFetchingListings && isUsingBackgroundListings) {
      return sortListingsInViewport(mapViewPort, fundCoveredProperties);
    }
    // When map is not shown or using cached data, return all filtered properties
    return fundCoveredProperties;
  }, [mapViewPort, sortListingsInViewport, fundCoveredProperties, shouldShowFullMap, isFetchingListings, isUsingBackgroundListings]);

  // Distance sort anchors on the user's searched location so the ordering matches the
  // distance shown on each card. Falls back to the event venue when no location was searched.
  const distanceSortAnchor = useMemo(() => {
    if (state.circularRegionSearch) {
      return {
        name: state.searchLocation ?? state.venue.name,
        location: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude },
      };
    }
    return state.venue;
  }, [state.circularRegionSearch, state.searchLocation, state.venue]);

  // Apply sorting based on selected sort option
  const { sortedListings, sortOption, updateSort } = useListingSort({
    filters,
    eventHandle,
    defaultSortOption,
    listings: listingsInViewport,
    sortAnchor: distanceSortAnchor,
    fundBalance: scenarioConfig.markerOutOfPocketBalance,
  });
  const shouldUseCouplePickMapMode = effectiveIsShowingAllCouplesPicks && hasAccommodationPlacesByCouple;

  const handleSearchFormSubmit = useEventCallback<UseAccommodationsConsoleSearchParamsStateReturn['handleHeaderSearchFormSubmit']>(value => {
    onHeaderSearchSubmit(value);
    resetFilters();
    recenterMapViaLocation(true);
  });

  /**
   * Generates map markers for the accommodation map to show the couple picks initially.
   * When showing all couples picks, includes:
   * - Markers for all hotels marked as picked by the couple
   * - The search center location marker (if a circular region search is active)
   * Returns an empty array when not showing couples picks.
   */
  const mapMarkers = useMemo(() => {
    if (!shouldUseCouplePickMapMode) {
      return [];
    }

    // Collect markers for all hotels picked by the couple
    const couplePickMarkers: ReadonlyArray<LatLngLiteral> = filteredProperties.reduce<ReadonlyArray<LatLngLiteral>>(
      (markers, hotel) => (hotel.isPickedByCouple ? [...markers, { lat: hotel.location.lat, lng: hotel.location.lng }] : markers),
      [] as ReadonlyArray<LatLngLiteral>,
    );

    // Add the search center location marker if available
    const searchCenterMarker: ReadonlyArray<LatLngLiteral> = state.circularRegionSearch
      ? [{ lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude }]
      : ([] as ReadonlyArray<LatLngLiteral>);

    return [...couplePickMarkers, ...searchCenterMarker] as const;
  }, [filteredProperties, shouldUseCouplePickMapMode, state.circularRegionSearch]);

  useEffect(() => {
    if (shouldUseCouplePickMapMode) {
      recenterMapViaMarkers();
    } else {
      recenterMapViaLocation();
    }
  }, [shouldUseCouplePickMapMode, recenterMapViaMarkers, recenterMapViaLocation]);

  const onChangeMapViewPort = useEventCallback((center: { lat: number; lng: number }, bounds: google.maps.LatLngBounds) => {
    setMapViewPort({ center, bounds });
  });

  const getRedirectUrl = useAccommodationRedirectUrl(state, sourceName);
  const listingTelemetry = useListingsPanelTelemetry();

  const parseUrl = useSafeUrlParser();
  // Same resolution the redirect URL uses, so the Amplitude event and the URL can't disagree.
  const mergedUtm = useAcquisitionAwareUtm(createUtmParametersForAccommodationsConsolePage(sourceName));

  // Defer to rAF to avoid forced reflow from interleaving layout reads with writes
  const scrollToSelectedHotel = useEventCallback((hotelId: Maybe<string>) => {
    requestAnimationFrame(() => {
      const hotelElement = document.getElementById(`listing-${hotelId}`);
      hotelElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  const onClickMarker = useEventCallback((hotel: AccommodationListingDTO) => {
    telemetry.mapMarkerClicked({
      listingType: hotel.type,
      placeId: hotel.joyPlaceId,
      hotelName: hotel.name,
      from: markerClickFrom ?? 'fullMap',
    });
    onMarkerClick?.(hotel);
    setSelectedHotel(hotel);
  });

  const handleOnTogglePanel = useEventCallback(() => {
    togglePanel(isOpen => !isOpen);
  });

  const handleResetFilters = useEventCallback(() => {
    resetFilters();
    setPropertyNameSearch('');
    // Cash-fund resets to its own default sort; guest/admin/ota keep the original reset behavior.
    updateSort(scenarioConfig.variant === 'cashFund' ? defaultSortOption : SortOption.COUPLES_PICKS);
    recenterMapViaLocation();
  });

  const noOfNights = useMemo(
    () => differenceInCalendarDays(new Date(state.searchDates.checkout), new Date(state.searchDates.checkin)),
    [state.searchDates.checkin, state.searchDates.checkout],
  );

  const { paginatedListings, showNextPageOfListings, hasMoreListingsToShow, listingsContainerRef } = usePagination({ listings: sortedListings, filters });

  const openHotelRedirectUrl = useEventCallback((hotel: AccommodationListingDTO) => {
    const redirectUrl = getRedirectUrl(hotel);
    if (!redirectUrl) return;
    listingTelemetry.seeAvailabilityClicked({ joyPlaceId: hotel?.joyPlaceId || '', hotelName: hotel?.name || '', isSponsored: hotel.isSponsored ?? false, chain: hotel.chain });
    const urlInfo = parseUrl(redirectUrl, 'AccommodationsConsole.handleHotelSelection');
    if (urlInfo) {
      listingTelemetry.hotelBookingRedirect({
        joyPlaceId: hotel?.joyPlaceId || '',
        hotelName: hotel?.name || '',
        destinationURL: redirectUrl,
        destinationDomain: urlInfo.hostname,
        destinationName: getSelectedRedirectTelemetryName(hotel.redirectMechanisms),
        isSponsored: hotel.isSponsored ?? false,
        chain: hotel.chain,
        ...mergedUtm,
      });
    }
    withWindow(global => {
      global.open(redirectUrl, '_blank');
    });
  });

  const handleHotelSelection = useEventCallback(
    (hotel: AccommodationListingDTO) => {
      // When onMarkerClick is provided, the caller owns the action (e.g. ExploreArea opens a drawer)
      if (onMarkerClick) return;
      // In MAP_ONLY mode without onMarkerClick, open the booking URL (works on both mobile and desktop)
      if (isMapOnlyMode) {
        openHotelRedirectUrl(hotel);
        return;
      }
      // On mobile, SingleHotelView opens automatically via the selectedHotel prop
      if (isMobileOrTablet) return;

      if (hidePanel) {
        openHotelRedirectUrl(hotel);
      } else {
        scrollToSelectedHotel(hotel.joyPlaceId);
      }
    },
    [isMobileOrTablet, scrollToSelectedHotel, openHotelRedirectUrl, hidePanel, isMapOnlyMode, onMarkerClick],
  );

  useEffect(() => {
    if (selectedHotel) {
      handleHotelSelection(selectedHotel);
      if (!isMobileOrTablet) {
        setSelectedHotel(null);
      }
    }
  }, [handleHotelSelection, isMobileOrTablet, selectedHotel]);

  useEffect(() => {
    if (selectedHotelId) {
      const hotel = sortedListings.find((h: AccommodationListingDTO) => h.joyPlaceId === selectedHotelId);
      if (!hotel) return;
      setSelectedHotel(hotel);
    }
  }, [selectedHotelId, sortedListings]);

  const handleShowOnMap = useEventCallback(() => {
    setIsMapExpanded(true);
  });

  // Functional setState removes the deps on hidePanel and togglePanel (Vercel 5.9)
  const handleShowList = useCallback(() => {
    setIsMapExpanded(false);
    togglePanel(() => false);
  }, []);

  // Reset map zoom to default when map is expanded from compact mode
  // This ensures the zoom is set even if auto-zoom happens after initial render
  useEffect(() => {
    if (isMapExpanded && isCompactMapMode) {
      // Use a delay to ensure the map is fully rendered and any auto-zoom has completed
      // before resetting to default zoom again
      const timer = setTimeout(() => {
        recenterMapViaLocation(true);
      }, 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isMapExpanded, isCompactMapMode, recenterMapViaLocation]);

  const mapLocation = useMemo(
    () =>
      state.circularRegionSearch && isValidCoordinates({ latitude: state.circularRegionSearch.latitude, longitude: state.circularRegionSearch.longitude })
        ? { latlng: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude } }
        : null,
    [state.circularRegionSearch],
  );

  const searchLocationForMap = useMemo(
    () => (state.searchLocation && mapLocation ? { name: state.searchLocation, location: { lat: mapLocation.latlng.lat, lng: mapLocation.latlng.lng } } : undefined),
    [state.searchLocation, mapLocation],
  );

  const { currency: currencyCode } = useTravelWhiteLabelCurrency();
  // Navigator language is stable for the lifetime of the page, so compute it once
  const navigatorLanguage = useMemo(() => readNavigatorLanguage(), []);

  // When a fund balance is configured (cash-fund variant), markers show the per-night out-of-pocket
  // "+$X" (max(0, stayTotal − balance) / nights) instead of the nightly price. Returns undefined for
  // listings with no stay total (room blocks/custom) so they keep the default price label.
  const markerOutOfPocketBalance = scenarioConfig.markerOutOfPocketBalance;
  const markerPriceLabelFor = useEventCallback((hotel: AccommodationListingDTO): string | undefined => {
    if (typeof markerOutOfPocketBalance !== 'number') return undefined;
    const values = hotel.pricingInfo?.values;
    const stayTotal = values?.totalRate;
    if (!values || typeof stayTotal !== 'number') return undefined;
    // Format in the listing's own currency/digits (not a hardcoded USD/2dp) so non-2-decimal currencies render correctly.
    const digits = values.totalRateDigits ?? 2;
    const markerCurrency = values.totalRateCurrency ?? currencyCode;
    const outOfPocketPerNight = Math.max(0, stayTotal - markerOutOfPocketBalance) / Math.max(1, noOfNights);
    return `+${translatedPrice(Math.round(outOfPocketPerNight * Math.pow(10, digits)), digits, markerCurrency, navigatorLanguage)}`;
  });

  const filtersPanelContent = useMemo(
    () => (
      <SkeletonGroup
        isReady={true}
        placeholder={<FiltersPanel.Skeleton showJoyTravelBenefitsFilter={showJoyTravelBenefitsFilter} />}
      >
        <FiltersPanel
          filters={filters}
          eventName={eventName}
          accentColor={scenarioConfig.accentColor}
          onFiltersChange={updateFilters}
          supportedFacilities={supportedFacilities}
          supportedBrands={supportedBrands}
          facilityCounts={facilityCounts}
          brandCounts={brandCounts}
          starRatingCounts={starRatingCounts}
          prices={listingPrices}
          guestRatingCounts={guestRatingCounts}
          propertyCategoryCounts={propertyCategoryCounts}
          isLoadingPrices={isFetchingListingPrices}
          hidePriceRangeFacet={!!pricingError}
          onClose={onFilterPanelClose}
          onReset={handleResetFilters}
          sortOption={sortOption}
          onSortChange={updateSort}
          currencyCode={currencyCode}
          navigatorLanguage={navigatorLanguage}
          dynamicPriceMax={dynamicPriceMax}
          mapData={
            isCompactMapMode && mapLocation
              ? {
                  location: mapLocation,
                  onShowOnMap: handleShowOnMap,
                  shouldShowFullMap: shouldShowFullMap,
                }
              : undefined
          }
          propertyNameSearch={propertyNameSearch}
          onPropertyNameSearchChange={setPropertyNameSearch}
          hideEventNameAndNote={hideEventNameAndNote}
          showAvailableOnlyTooltip={showAvailableOnlyTooltip}
          showJoyTravelBenefitsFilter={showJoyTravelBenefitsFilter}
          includeCouplesPicks={includeCouplesPicks}
          isPanelVisible={!isMobileOrTablet || isFilterPanelOpen}
          renderExtraFilter={renderFiltersHeader}
          showFundSavingsSort={showFundSavingsSort}
        />
      </SkeletonGroup>
    ),
    [
      filters,
      eventName,
      updateFilters,
      supportedFacilities,
      supportedBrands,
      facilityCounts,
      brandCounts,
      starRatingCounts,
      listingPrices,
      guestRatingCounts,
      propertyCategoryCounts,
      isFetchingListingPrices,
      pricingError,
      onFilterPanelClose,
      handleResetFilters,
      sortOption,
      updateSort,
      currencyCode,
      navigatorLanguage,
      dynamicPriceMax,
      isCompactMapMode,
      mapLocation,
      handleShowOnMap,
      shouldShowFullMap,
      propertyNameSearch,
      setPropertyNameSearch,
      hideEventNameAndNote,
      showAvailableOnlyTooltip,
      showJoyTravelBenefitsFilter,
      includeCouplesPicks,
      isMobileOrTablet,
      isFilterPanelOpen,
      renderFiltersHeader,
      showFundSavingsSort,
      scenarioConfig.accentColor,
    ],
  );

  const listingsPanelContent = useMemo(
    () => (
      <SkeletonGroup
        isReady={!isFetchingListings}
        overflowY={{ _: undefined, md: 'auto' }}
        width={{ _: '100%', md: hidePanel ? 0 : 'fit-content' }}
        paddingBottom={9}
        placeholder={<ListingsPanel.Skeleton isMobileOrTablet={isMobileOrTablet} />}
        rootRef={listingsContainerRef}
      >
        {renderListingsHeader?.()}
        {paginatedListings.length > 0 || hasApiError ? (
          <>
            <ListingsPanel
              state={state}
              sortOption={sortOption}
              onSortChange={updateSort}
              noOfNights={noOfNights}
              filters={filters}
              updateFilters={updateFilters}
              listings={paginatedListings}
              isLoadingPrices={isFetchingListingPrices}
              hidePanel={hidePanel}
              hasError={hasApiError}
              sourceName={sourceName}
              includeCouplesPicks={includeCouplesPicks}
              renderListingEntry={renderListingEntry}
              currencyCode={currencyCode}
              navigatorLanguage={navigatorLanguage}
              dynamicPriceMax={dynamicPriceMax}
              showFundSavingsSort={showFundSavingsSort}
              accentColor={scenarioConfig.accentColor}
            />
            {hasMoreListingsToShow && !hasApiError && <ShowMoreButton onClick={showNextPageOfListings} />}
          </>
        ) : (
          <EmptyState
            onReset={handleResetFilters}
            showResetButton={!!cachedListings?.length}
          />
        )}
      </SkeletonGroup>
    ),
    [
      isFetchingListings,
      isMobileOrTablet,
      hidePanel,
      listingsContainerRef,
      paginatedListings,
      hasApiError,
      state,
      sortOption,
      updateSort,
      noOfNights,
      filters,
      updateFilters,
      isFetchingListingPrices,
      hasMoreListingsToShow,
      showNextPageOfListings,
      handleResetFilters,
      cachedListings?.length,
      sourceName,
      includeCouplesPicks,
      renderListingEntry,
      renderListingsHeader,
      currencyCode,
      navigatorLanguage,
      dynamicPriceMax,
      showFundSavingsSort,
      scenarioConfig.accentColor,
    ],
  );

  const mapHotels = isFetchingListings ? [] : paginatedListings;

  // Defer mounting the Map component (and its Google Maps SDK load) in desktop compact
  // mode until the user clicks "Show on Map". On mobile the map is always behind the
  // drawer so we mount it immediately to avoid a blank area when the drawer is swiped down.
  // See TRAV-2638 for performance analysis.
  const shouldDeferMap = isCompactMapMode && !shouldShowFullMap;

  const hasValidMapLocation = state.circularRegionSearch && isValidCoordinates({ latitude: state.circularRegionSearch.latitude, longitude: state.circularRegionSearch.longitude });

  // An external onMarkerClick means the caller (e.g. admin ExploreArea) owns the marker-click UX,
  // so the built-in mobile pan for SingleHotelView shouldn't run.
  const isCallerOwnedMarkerClick = !!onMarkerClick;

  const mapContent =
    hasValidMapLocation && !shouldDeferMap ? (
      <Map
        location={{ latlng: { lat: state.circularRegionSearch!.latitude, lng: state.circularRegionSearch!.longitude } }}
        hotels={mapHotels}
        isFetchingHotels={isFetchingListings}
        onChangeLocation={onChangeLocation}
        onChangeMapViewPort={onChangeMapViewPort}
        onClickMarker={onClickMarker}
        isCouplePick={shouldUseCouplePickMapMode}
        onToggleLeftPanel={isMapOnlyMode ? undefined : handleOnTogglePanel}
        setReCenterMap={setReCenterMap}
        selectedMarker={selectedHotel?.joyPlaceId || ''}
        reCenterMap={reCenterMap}
        hidePanel={hidePanel}
        mapMarkers={isFetchingListings ? [] : mapMarkers}
        venue={state.venue}
        searchLocation={searchLocationForMap}
        shouldShowVenueMapPin={shouldShowVenueMapPin}
        skipAutoFitAfterUserInteraction={!shouldUseCouplePickMapMode}
        searchCtaTopOffset={scenarioConfig.searchCtaTopOffset}
        skipMobileMarkerPan={isCallerOwnedMarkerClick}
        markerColor={scenarioConfig.markerColor}
        markerPriceLabelFor={markerPriceLabelFor}
      />
    ) : null;

  if (isMapOnlyMode) {
    return (
      <Box
        position="relative"
        width="100%"
        height="100%"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          zIndex="docked"
        >
          <ProgressiveLoader
            isLoading={isCombinedLoading}
            accentColor={scenarioConfig.accentColor}
          />
        </Box>
        <AccommodationMapListSync>
          {mapContent}
          {isFetchingListings && !state.circularRegionSearch && <MapLoader />}
        </AccommodationMapListSync>
      </Box>
    );
  }

  return (
    <Container>
      <Box ref={headerRef}>
        <AccommodationsConsoleHeader
          state={state}
          eventHandle={eventHandle}
          onClose={onClose}
          onSearchOpen={onSearchOpen}
          onFilterPanelOpen={onFilterPanelOpen}
          handleFormSubmit={handleSearchFormSubmit}
          topBar={topBar}
          searchCtaColor={scenarioConfig.accentColor}
        />
      </Box>
      {!isMobileOrTablet && (
        <ProgressiveLoader
          isLoading={isCombinedLoading}
          accentColor={scenarioConfig.accentColor}
        />
      )}
      {/* Main Content */}
      {isMobileOrTablet && hasApiError && <ErrorState />}
      {isMobileOrTablet && !hasApiError && (
        <AccommodationMapListSync>
          <MobileMapDrawerLayout
            headerHeight={headerSize.height}
            listingsPanelContent={listingsPanelContent}
            drawerBackgroundColor={scenarioConfig.pageBackgroundColor}
          >
            {mapContent}
            {isFetchingListings && !state.circularRegionSearch && <MapLoader />}
          </MobileMapDrawerLayout>
          <BottomSheet
            isOpen={isFilterPanelOpen}
            onClose={onFilterPanelClose}
          >
            {filtersPanelContent}
          </BottomSheet>
          <BottomSheet
            isOpen={isSearchOpen}
            onClose={onSearchClose}
          >
            <MobileSearchComponent
              onClose={onSearchClose}
              accentColor={scenarioConfig.accentColor}
              state={state}
              handleFormSubmit={handleSearchFormSubmit}
              searchForm={topBar.searchForm}
            />
          </BottomSheet>
          {selectedHotel && (
            <SingleHotelView
              onClose={() => setSelectedHotel(null)}
              listings={paginatedListings}
              selectedHotelId={selectedHotel.joyPlaceId}
              state={state}
              noOfNights={noOfNights}
              sourceName={sourceName}
              renderListingEntry={renderListingEntry}
            />
          )}
        </AccommodationMapListSync>
      )}
      {!isMobileOrTablet && (
        <Content>
          {isFetchingListings && isInitialLoad ? (
            <Flex
              width="100%"
              height={'100%'}
              justifyContent="center"
              alignItems="center"
            >
              <AccommodationsConsoleLoader />
            </Flex>
          ) : (
            <AccommodationMapListSync>
              {isCompactMapMode ? (
                <Flex
                  width="100%"
                  height="100%"
                  overflowY="auto"
                  justifyContent={shouldShowFullMap ? 'flex-start' : 'center'}
                  alignItems="flex-start"
                  transition="all 0.3s ease-in-out"
                  position="relative"
                >
                  <Box
                    position="sticky"
                    top={0}
                    alignSelf="flex-start"
                    height="fit-content"
                    maxHeight="100%"
                    overflowY="auto"
                    overflowX="hidden"
                    zIndex={1}
                  >
                    {filtersPanelContent}
                  </Box>
                  {listingsPanelContent}
                  {shouldShowFullMap && mapContent ? (
                    <Box
                      flex="1"
                      height="100%"
                      position="sticky"
                      top={0}
                      alignSelf="flex-start"
                      transition="all 0.3s ease-in-out"
                      style={{
                        animation: 'slideIn 0.3s ease-in-out',
                      }}
                    >
                      <Box
                        position="absolute"
                        top={7}
                        right={7}
                        zIndex="docked"
                      >
                        <ListButton onClick={handleShowList} />
                      </Box>
                      {mapContent}
                    </Box>
                  ) : null}
                </Flex>
              ) : (
                <>
                  {filtersPanelContent}
                  {listingsPanelContent}
                  {mapContent}
                </>
              )}
            </AccommodationMapListSync>
          )}
        </Content>
      )}
    </Container>
  );
};

const Container = ({ children }: { children?: React.ReactNode }) => {
  return <>{children}</>;
};

const Content = ({ children }: { children: React.ReactNode }) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  return (
    <Flex
      overflow="auto"
      flex={1}
      // flex:1 fills remaining space after the header inside the SkeletonGroup flex column;
      // minHeight:0 on desktop overrides the flex default (content-sized) so overflow scrolling works correctly
      minHeight={isMobileOrTablet ? '400px' : 0}
    >
      {children}
    </Flex>
  );
};

export const AccommodationsConsole = ({
  eventHandle,
  eventName,
  onClose,
  selectedHotelId,
  scenarioConfig,
  renderListingEntry,
  renderListingsHeader,
  renderFiltersHeader,
  onMarkerClick,
  onListingsSettled,
  markerClickFrom,
}: AccommodationsConsoleProps) => {
  const scenarioFlags = resolveScenarioFlags(scenarioConfig.variant);
  const { defaultSortOption, shouldShowAllAccommodations, showJoyTravelBenefitsFilter } = scenarioFlags;
  const { state, loading, handleOnChangeLocation, handleHeaderSearchFormSubmit, isInitialLoad, isShowingAllCouplesPicks } = useAccommodationsConsoleSearchParamsState({
    eventHandle,
    shouldShowAllAccommodations,
    defaultSortOption,
  });
  const isMobileOrTablet = useIsMobileOrTablet();

  const { pageViewed } = useAccommodationsConsoleTelemetry();

  useMount(() => {
    pageViewed();
  });

  return (
    <JoyKitThemeProvider>
      <HotelOfferDiagnosticsProvider>
        <SkeletonGroup
          isReady={!loading || !!state}
          flexDirection="column"
          width={'100%'}
          height={scenarioConfig.fillParent ? '100%' : '100dvh'}
          maxWidth="100%"
          overflow={scenarioConfig.fillParent ? 'hidden' : 'auto'}
          display="flex"
          // NB: use `background` not `backgroundColor` — SkeletonGroup destructures `backgroundColor`
          // for its shimmer base, so it never reaches the content root.
          background={scenarioConfig.pageBackgroundColor}
          placeholder={
            <Container>
              <AccommodationsConsoleHeader.Skeleton />
              <Content>
                <FiltersPanel.Skeleton showJoyTravelBenefitsFilter={showJoyTravelBenefitsFilter} />
                <ListingsPanel.Skeleton isMobileOrTablet={isMobileOrTablet} />
              </Content>
            </Container>
          }
        >
          {state && (
            <AccommodationsConsoleErrorBoundary>
              <Root
                state={state}
                eventHandle={eventHandle}
                eventName={eventName}
                onClose={onClose}
                scenarioConfig={scenarioConfig}
                scenarioFlags={scenarioFlags}
                onChangeLocation={handleOnChangeLocation}
                onHeaderSearchSubmit={handleHeaderSearchFormSubmit}
                selectedHotelId={selectedHotelId}
                isInitialLoad={isInitialLoad}
                isShowingAllCouplesPicks={isShowingAllCouplesPicks}
                renderListingEntry={renderListingEntry}
                renderListingsHeader={renderListingsHeader}
                renderFiltersHeader={renderFiltersHeader}
                onMarkerClick={onMarkerClick}
                onListingsSettled={onListingsSettled}
                markerClickFrom={markerClickFrom}
              />
            </AccommodationsConsoleErrorBoundary>
          )}
        </SkeletonGroup>
      </HotelOfferDiagnosticsProvider>
    </JoyKitThemeProvider>
  );
};

export const AccommodationsConsoleWithHealth = wrapWithAccommodationsConsoleProvider(AccommodationsConsole);
